import Big from 'big.js'
import {
  ChangeEvent,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { IStakes } from '../../../hooks/useMasterContract'
import { selectIsDarkThemeState } from '../../../modules/miscUi/selectors'
import {
  selectAccount,
  selectBalance,
  selectBalanceEth,
  selectIsEthRaise,
} from '../../../modules/web3/selectors'
import { Box } from '../../../shared_components/UI/Box'
import { IPool } from '../../../types/reducerTypes'
import { amountFromWei } from '../../../utils/amount'

import { BoxSubHeading, PoolInput } from '../parts'
import {
  setInvestmentAmountForCheckout,
  setIsCheckoutScreen,
} from '../../../modules/miscUi/actions'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { USDB_DECIMALS } from '../../../config'
import useNetworkId from '../../../hooks/useNetworkId'
import { ThemedButton } from '../../../shared_components/ThemedButton'
import useWeb3, { NETWORK_VERSION } from '../../../hooks/useWeb3'
import { useKycStatus } from '../Checkout/KYC'

const InputContainer = styled(Box)`
  position: relative;
  justify-content: center;
  align-items: center;
`

const Max = styled.button`
  position: absolute;
  right: 16px;
  font-size: 16px;
  line-height: 20px;
  opacity: 0.6;
  text-transform: uppercase;
  :hover {
    opacity: 0.8;
  }
`

export const DialogContainer = styled(Box)`
  padding: 0;
  align-items: center;
`

export const Dialog = styled.span<{ isWarning?: boolean }>`
  font-family: 'Space Grotesk';
  font-size: 14px;
  line-height: 20px;
  color: ${({ isWarning, theme }) =>
    isWarning ? theme.colors.dialogueError : theme.colors.dialogueSuccess};
`

export const DialogIcon = styled.img`
  margin-right: 8px;
`

interface Props {
  maxAllocation: number | null
  stakes: IStakes | undefined
  fetchStakes: () => Promise<void>
  remainingAllocation: string
  pool: IPool
  isLoading: boolean
  setIsLoading: Dispatch<SetStateAction<boolean>>
}

const Open: FC<Props> = ({ maxAllocation, remainingAllocation, pool }) => {
  const isDarkTheme = useSelector(selectIsDarkThemeState)
  const balanceUsdb = useSelector(selectBalance)
  const balanceInEth = useSelector(selectBalanceEth)
  const isEthRaise = useSelector(selectIsEthRaise)
  const balance = new Big(isEthRaise ? balanceInEth : balanceUsdb)
  const account = useSelector(selectAccount)

  const balanceInUnits = amountFromWei(balance, USDB_DECIMALS).toNumber()

  const history = useHistory()
  const { url } = useRouteMatch()

  const dispatch = useDispatch()

  const { initWeb3 } = useWeb3()

  const [investAmount, setInvestAmount] = useState<string>('')
  const [warningDialog, setWarningDialog] = useState<string | null>(null)
  const [successDialog, setSuccessDialog] = useState<string | null>(null)

  const isKyc = !!pool?.poolSettings?.requiresKYC

  const { label: kycStatusLabel, kycComplete, kycNotStarted } = useKycStatus()

  const { wrongNetwork, changeNetwork } = useNetworkId()

  const noWallet = !account || account === ''

  const dismissDialog = () => setWarningDialog(null)

  const setMax = () => {
    setInvestAmount(
      String(
        maxAllocation
          ? parseFloat(remainingAllocation) !== maxAllocation
            ? Math.min(balanceInUnits, Number(remainingAllocation))
            : Math.min(balanceInUnits, Number(maxAllocation))
          : 0,
      ),
    )
  }

  const investTo = useCallback(async () => {
    const warn = (text: string = 'There was an error, please check MetaMask') =>
      setWarningDialog(text)
    if (Number(investAmount) > 0) {
      dispatch(setInvestmentAmountForCheckout(parseInt(investAmount)))
      history.push(`${url}/checkout?invest_amount=${investAmount}`)
      dispatch(setIsCheckoutScreen(true))
    } else {
      warn('Please select an amount to invest')
    }
  }, [dispatch, history, investAmount, url])

  const handleInvestChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setInvestAmount(value)
    dismissDialog()
  }

  useEffect(() => {
    if (!maxAllocation) return

    const useRemaining = Number(remainingAllocation) !== maxAllocation

    if (
      useRemaining
        ? Number(investAmount) > Number(remainingAllocation)
        : Number(investAmount) > maxAllocation
    ) {
      setWarningDialog(
        `Maximum allocation ${
          useRemaining ? Number(remainingAllocation) : maxAllocation
        }`,
      )
      setInvestAmount(
        useRemaining ? remainingAllocation : maxAllocation.toString(),
      )
      return
    }
  }, [investAmount, remainingAllocation, maxAllocation])

  const isInvestDisabled =
    remainingAllocation === '0' || (isKyc && !kycComplete)

  const handleBuy = useCallback(() => {
    if (noWallet) {
      initWeb3()
      return
    }

    if (wrongNetwork) {
      changeNetwork(NETWORK_VERSION)
      return
    }

    investTo()
  }, [noWallet, wrongNetwork, initWeb3, changeNetwork, investTo])

  function navigateToKyc() {
    history.push('/account#kyc')
  }

  return (
    <Box direction="column" marginTop={12} g={16}>
      <Box dc g={4}>
        <BoxSubHeading fullOpacity>Invest</BoxSubHeading>
        <InputContainer>
          <PoolInput
            type="number"
            value={investAmount}
            onChange={handleInvestChange}
            placeholder={''}
            max={
              parseFloat(remainingAllocation) !== maxAllocation
                ? Math.min(balanceInUnits, Number(remainingAllocation))
                : Math.min(balanceInUnits, Number(maxAllocation))
            }
            min={0}
            disabled={isInvestDisabled}
          />
          {remainingAllocation !== '0' && (
            <Max disabled={noWallet} onClick={setMax}>
              use max
            </Max>
          )}
        </InputContainer>
      </Box>
      {isKyc && kycNotStarted ? (
        <ThemedButton onClick={navigateToKyc} w="100%">
          KYC REQUIRED
        </ThemedButton>
      ) : (
        <ThemedButton
          onClick={isKyc && !kycComplete ? navigateToKyc : handleBuy}
          disabled={isInvestDisabled || !investAmount.trim()}
          w="100%"
        >
          {isKyc && !kycComplete
            ? `KYC ${kycStatusLabel}`
            : noWallet
            ? 'Connect & Buy'
            : wrongNetwork
            ? 'Switch to blast'
            : 'Buy'}
        </ThemedButton>
      )}
      {warningDialog && (
        <DialogContainer>
          <DialogIcon src={`/img/icons/dialog-cross.svg`} />
          <Dialog isWarning>{warningDialog}</Dialog>
        </DialogContainer>
      )}
      {successDialog && (
        <DialogContainer>
          <DialogIcon
            src={`/img/icons/dialog-tick${isDarkTheme ? '' : '-light'}.svg`}
          />
          <Dialog>{successDialog}</Dialog>
        </DialogContainer>
      )}
    </Box>
  )
}

export default Open
